/**
 * AEWhisperClean — первый шаг пресета «Шёпот и тихая речь» /audio-enhancer:
 * шум вычитается по ОБРАЗЦУ из паузы этой же записи, без нейросети.
 *
 * Зачем. Шёпот звучит без голосового тона, и нейросетевой шумодав (DeepFilterNet
 * у /denoise, «ИИ» здесь) глушит его как шум: шёпот уходит на 12 дБ ниже голоса,
 * у слушателя ESTOI 0.59/0.34 → 0.15/0.05 (чат Премиума 705, 22.10.2026). Шумодав
 * по уровню (afftdn с полом) на записи, где гул стоит вровень с шёпотом, делает
 * хуже исходника. Вычитание по образцу паузы (RefDenoiseProcessor — та же
 * «Точная чистка по образцу шума», что в /denoise) + вырезы на тонах гула —
 * единственный путь, который не хуже исходника на всех стимулах стенда
 * tests/asr-bench/whisper-dialog; после него тихие фразы поднимает ведущий
 * LoudnessStage «whisper». Разбор — docs/why/whisper-under-denoise.md.
 *
 * Как. Два прохода по записи блоками по 30 с (AudioRangeSource: файл
 * смонтирован в FFmpeg, в память не раскодируется):
 *   1. RMS кадров по 1024 отсчёта → пол = среднее тихих 20 % → самый длинный ход
 *      кадров тише 1,8 × пола (≥ 0,5 с, берётся до 8 с) — образец; цифровой ноль
 *      в образец не идёт. Тот же поиск, что в /denoise, но по ВСЕЙ записи.
 *   2. Профиль образца → поток вычитания (α, β по силе) → WAV 24 бит
 *      (OutputSink, на диск, когда вырастает). Тихая запись (речь на −65 dBFS)
 *      в 16 битах упёрлась бы в шум квантования.
 * Тона гула (40–1200 Гц, на 10 дБ выше соседей) ищутся в спектре образца — их
 * вырезы ставит цепочка пресета.
 *
 *   var r = await AEWhisperClean.prepare(file, deps, { strength, onProgress, isCancelled });
 *   r.file   → File (audio/wav)    r.region → {start, end} секунды или null
 *   r.tones  → [Гц]                r.stats  → { sec, ms, floorDb }
 * deps: { FFmpegProcessor, AudioRangeSource, OutputSink, RefDenoiseProcessor }.
 * Без образца (записи без пауз) вычитания нет — r.file === null, пресет
 * выравнивает исходник.
 */
(function (root) {
    'use strict';

    var SR = 48000;
    var FRAME = 1024;
    var BLOCK_S = 30;
    var QUIET_MULT = 1.8;
    var MIN_RUN_S = 0.5;
    var MAX_RUN_S = 8;
    var DIGITAL_ZERO = 1e-6;
    // Сила → (α, β) вычитания. Средняя — замеренный выбор (на шуме обращения
    // внятный шёпот 0.707 → 0.795, тихий 0.382 → 0.424); сильнее — глубже и с
    // музыкальным шумом, слабее — мягче.
    var STRENGTH = { light: [1.2, 0.15], medium: [1.5, 0.1], strong: [2.0, 0.05] };

    function abortErr() {
        var e = new Error('cancelled'); e.name = 'AbortError'; e.code = 'USER_CANCELLED'; return e;
    }

    /** Образец шума по RMS кадров: {startFrame, endFrame} или null. */
    function findNoiseRun(rms, sampleRate, frame) {
        frame = frame || FRAME;
        var live = [], i;
        for (i = 0; i < rms.length; i++) if (rms[i] > DIGITAL_ZERO) live.push(rms[i]);
        if (!live.length) return null;
        live.sort(function (a, b) { return a - b; });
        var k = Math.max(1, Math.floor(live.length / 5)), floor = 0;
        for (i = 0; i < k; i++) floor += live[i];
        floor /= k;
        var thr = floor * QUIET_MULT, best = 0, bestAt = -1, cur = 0, curAt = -1;
        for (i = 0; i <= rms.length; i++) {
            var q = i < rms.length && rms[i] > DIGITAL_ZERO && rms[i] < thr;
            if (q) { if (!cur) curAt = i; cur++; }
            else { if (cur > best) { best = cur; bestAt = curAt; } cur = 0; }
        }
        if (best * frame / sampleRate < MIN_RUN_S) return null;
        var cap = Math.floor(MAX_RUN_S * sampleRate / frame);
        return { startFrame: bestAt, endFrame: bestAt + Math.min(best, cap), floor: floor };
    }

    // Радикс-2 БПФ на месте (re, im — Float64Array длиной 2^k).
    function fft(re, im) {
        var n = re.length, i, j, k;
        for (i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) { var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
        }
        for (var size = 2; size <= n; size <<= 1) {
            var half = size >> 1, ang = -2 * Math.PI / size, wr = Math.cos(ang), wi = Math.sin(ang);
            for (i = 0; i < n; i += size) {
                var cr = 1, ci = 0;
                for (k = 0; k < half; k++) {
                    var a = i + k, b = a + half;
                    var xr = re[b] * cr - im[b] * ci, xi = re[b] * ci + im[b] * cr;
                    re[b] = re[a] - xr; im[b] = im[a] - xi; re[a] += xr; im[a] += xi;
                    var ncr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = ncr;
                }
            }
        }
    }

    /** Тона гула в образце: до `maxN` частот (Гц), 40–1200 Гц, на 10 дБ над соседями. */
    function findTones(x, sampleRate, maxN) {
        maxN = maxN || 4;
        var N = 32768;
        while (N > 4096 && N > x.length) N >>= 1;
        if (x.length < 2048) return [];
        var hop = N >> 1, segs = 0, P = new Float64Array(N / 2 + 1), w = new Float64Array(N), i, s;
        for (i = 0; i < N; i++) w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / N);
        for (s = 0; s + N <= Math.max(N, x.length); s += hop) {
            var re = new Float64Array(N), im = new Float64Array(N), mean = 0, m = Math.min(N, x.length - s);
            for (i = 0; i < m; i++) mean += x[s + i];
            mean /= Math.max(1, m);
            for (i = 0; i < m; i++) re[i] = (x[s + i] - mean) * w[i];
            fft(re, im);
            for (i = 0; i <= N / 2; i++) P[i] += re[i] * re[i] + im[i] * im[i];
            segs++;
            if (s + N >= x.length) break;
        }
        var d = new Float64Array(P.length), df = sampleRate / N;
        for (i = 0; i < P.length; i++) d[i] = 10 * Math.log10(P[i] / Math.max(1, segs) + 1e-30);
        var found = [];
        for (i = 5; i < d.length - 41; i++) {
            var f = i * df;
            if (f < 40 || f > 1200) continue;
            var isMax = true;
            for (var q = i - 3; q <= i + 3; q++) if (d[q] > d[i]) { isMax = false; break; }
            if (!isMax) continue;
            // соседи по обе стороны; у самого низа (50 Гц — 34-й бин) — только сверху
            var nb = [];
            if (i > 40) for (q = i - 40; q <= i - 4; q++) nb.push(d[q]);
            for (q = i + 4; q <= i + 40; q++) nb.push(d[q]);
            nb.sort(function (a, b) { return a - b; });
            var med = (nb[(nb.length - 1) >> 1] + nb[nb.length >> 1]) / 2;
            if (d[i] > med + 10) found.push({ prom: d[i] - med, f: Math.round(f) });
        }
        found.sort(function (a, b) { return b.prom - a.prom; });
        return found.slice(0, maxN).map(function (t) { return t.f; });
    }

    function mono(chans) {
        if (chans.length === 1) return chans[0];
        var n = chans[0].length, out = new Float32Array(n), c, i;
        for (c = 0; c < chans.length; c++) for (i = 0; i < n; i++) out[i] += chans[c][i] / chans.length;
        return out;
    }

    async function prepare(file, deps, opts) {
        opts = opts || {};
        var onProgress = opts.onProgress || function () {};
        var isCancelled = opts.isCancelled || function () { return false; };
        var t0 = Date.now();
        var ab = STRENGTH[opts.strength] || STRENGTH.medium;
        var src = await deps.AudioRangeSource.open(file, { FFmpegProcessor: deps.FFmpegProcessor });
        var sink = null, done = false;
        try {
            var ch = Math.max(1, Math.min(2, (src.info && src.info.channels) || 1));
            var total = Math.max(1, Math.round(((src.info && src.info.duration) || 0) * SR));
            var block = BLOCK_S * SR, at, chans, got, i;
            // 1. RMS кадров по всей записи
            var rms = [], carry = new Float32Array(0);
            for (at = 0; ; ) {
                if (isCancelled()) throw abortErr();
                chans = await src.read(at, block, SR, ch);
                got = chans[0].length;
                var m = mono(chans), buf = m;
                if (carry.length) { buf = new Float32Array(carry.length + m.length); buf.set(carry); buf.set(m, carry.length); }
                var whole = Math.floor(buf.length / FRAME);
                for (i = 0; i < whole; i++) {
                    var e = 0;
                    for (var j = 0; j < FRAME; j++) { var v = buf[i * FRAME + j]; e += v * v; }
                    rms.push(Math.sqrt(e / FRAME));
                }
                carry = buf.slice(whole * FRAME);
                at += got;
                onProgress(0.3 * Math.min(1, at / total), 'scan');
                if (got < block) break;
            }
            var run = findNoiseRun(rms, SR, FRAME);
            if (!run) {
                done = true;
                return { file: null, region: null, tones: [], stats: { sec: at / SR, ms: Date.now() - t0, floorDb: null } };
            }
            var a0 = run.startFrame * FRAME, a1 = run.endFrame * FRAME;
            var sample = await src.read(a0, a1 - a0, SR, ch);
            var tones = findTones(mono(sample), SR, 4);
            var noiseBuf = { sampleRate: SR, numberOfChannels: ch, length: sample[0].length,
                             getChannelData: function (c) { return sample[c]; } };
            var profile = deps.RefDenoiseProcessor.learn(noiseBuf, 0, sample[0].length / SR);
            var stream = deps.RefDenoiseProcessor.createStream(profile, { alpha: ab[0], beta: ab[1] });
            // 2. вычитание по образцу
            sink = await deps.OutputSink.create({ mime: 'audio/wav', name: 'whisper-clean' });
            var writer = deps.OutputSink.wavWriter(sink, SR, ch, 24);
            for (at = 0; ; ) {
                if (isCancelled()) throw abortErr();
                chans = await src.read(at, block, SR, ch);
                got = chans[0].length;
                if (got) {
                    var cleaned = stream.push(chans);
                    if (cleaned[0].length) await writer.writeChunk(cleaned);
                    at += got;
                }
                onProgress(0.3 + 0.7 * Math.min(1, at / total), 'clean');
                if (got < block) break;
            }
            var tail = stream.flush();
            if (tail[0].length) await writer.writeChunk(tail);
            var blob = await writer.finalize();
            done = true;
            var name = String((file && file.name) || 'audio').replace(/\.[^.]+$/, '') + '-whisper.wav';
            var out = (typeof File === 'function') ? new File([blob], name, { type: 'audio/wav' }) : blob;
            return { file: out, region: { start: a0 / SR, end: a1 / SR }, tones: tones,
                     stats: { sec: at / SR, ms: Date.now() - t0, floorDb: +(20 * Math.log10(run.floor + 1e-12)).toFixed(1) } };
        } finally {
            try { await src.close(); } catch (_) {}
            if (!done && sink && sink.cleanup) { try { await sink.cleanup(); } catch (_) {} }
        }
    }

    root.AEWhisperClean = { prepare: prepare, findNoiseRun: findNoiseRun, findTones: findTones, STRENGTH: STRENGTH };
    if (typeof module !== 'undefined' && module.exports) module.exports = root.AEWhisperClean;
})(typeof window !== 'undefined' ? window : globalThis);
